import { Link } from "react-router-dom";
import useSeo, { buildBreadcrumb } from "../lib/useSeo";

export default function Privacidad() {
  useSeo({
    title: "Política de Privacidad · Ley 21.719 · DoSoft",
    description: "Política de tratamiento de datos personales de DoSoft Ingeniería de Software, cumpliendo con la Ley 21.719 de protección de datos personales de Chile.",
    canonical: "https://dosoft.cl/privacidad",
    jsonLd: [
      buildBreadcrumb([
        { name: "Inicio", url: "/" },
        { name: "Privacidad", url: "/privacidad" },
      ]),
    ],
  });
  return (
    <div data-testid="privacidad-page" className="py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6">
          Legal · Ley 21.719 Chile
        </div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter text-ash-100 leading-[0.95] mb-4">
          Política de Privacidad
        </h1>
        <p className="text-ash-300 text-sm font-mono mb-12">
          Última actualización: Mayo 2026
        </p>

        <div className="space-y-10 text-ash-100 leading-relaxed">
          <Section title="1. Responsable del tratamiento">
            <p>
              DoSoft Ingeniería de Software (en adelante "DoSoft") es responsable del
              tratamiento de los datos personales que recopila a través de esta
              plataforma web, en conformidad con la Ley N° 21.719 sobre Protección de
              Datos Personales de Chile.
            </p>
          </Section>

          <Section title="2. Datos que recopilamos">
            <p>Recopilamos los siguientes datos cuando interactúas con la plataforma:</p>
            <ul className="space-y-2 mt-3 pl-4">
              <Bullet>Identificación: nombre, email, teléfono, empresa, RUT cuando aplica.</Bullet>
              <Bullet>Información del proyecto: descripción, industria, plazos y presupuesto que nos compartas en el cotizador o formulario de contacto.</Bullet>
              <Bullet>Datos técnicos: dirección IP, navegador, fecha y hora de envío del formulario, registrados con fines de seguridad.</Bullet>
            </ul>
          </Section>

          <Section title="3. Finalidad del tratamiento">
            <p>Los datos se utilizan exclusivamente para:</p>
            <ul className="space-y-2 mt-3 pl-4">
              <Bullet>Contactarte para preparar una propuesta comercial.</Bullet>
              <Bullet>Dar seguimiento al proyecto y mantener la relación comercial.</Bullet>
              <Bullet>Cumplir obligaciones legales, contables y tributarias.</Bullet>
              <Bullet>Mejorar nuestros servicios mediante análisis agregado y anónimo.</Bullet>
            </ul>
          </Section>

          <Section title="4. Base legal">
            <p>
              El tratamiento se realiza sobre la base del <strong>consentimiento explícito</strong>{" "}
              que entregas al iniciar el cotizador o enviar el formulario de contacto.
              Sin este consentimiento no almacenamos información personal alguna.
            </p>
          </Section>

          <Section title="5. Conservación de los datos">
            <p>
              Conservamos tus datos mientras dure la relación comercial o hasta que
              solicites su eliminación. Datos contables y de facturación se conservan
              por el plazo que exija la ley tributaria chilena.
            </p>
          </Section>

          <Section title="6. Cesión a terceros">
            <p>
              <strong>No vendemos ni compartimos tus datos con terceros</strong> sin tu autorización
              explícita, salvo cuando sea necesario por obligación legal o para proveer
              el servicio (p. ej. proveedores tecnológicos de infraestructura sujetos a
              acuerdos de confidencialidad).
            </p>
          </Section>

          <Section title="7. Tus derechos">
            <p>De acuerdo a la Ley 21.719, tienes derecho a:</p>
            <ul className="space-y-2 mt-3 pl-4">
              <Bullet><strong>Acceso:</strong> saber qué datos tuyos tenemos.</Bullet>
              <Bullet><strong>Rectificación:</strong> corregir información inexacta.</Bullet>
              <Bullet><strong>Eliminación:</strong> que borremos tus datos cuando ya no sean necesarios.</Bullet>
              <Bullet><strong>Oposición:</strong> oponerte al tratamiento por motivos legítimos.</Bullet>
              <Bullet><strong>Portabilidad:</strong> recibir tus datos en formato estructurado.</Bullet>
              <Bullet><strong>Revocación del consentimiento</strong> en cualquier momento.</Bullet>
            </ul>
            <p className="mt-3">
              Para ejercer cualquiera de estos derechos, contáctanos desde la
              plataforma a través del{" "}
              <Link to="/contacto" className="text-brand hover-line">formulario de contacto</Link>.
              Atendemos tu solicitud dentro de los plazos que dispone la ley.
            </p>
          </Section>

          <Section title="8. Seguridad">
            <p>
              Aplicamos medidas técnicas y organizativas razonables para proteger tus
              datos: cifrado en tránsito (HTTPS), control de acceso al panel
              administrativo mediante autenticación, registro de actividad y respaldo
              periódico.
            </p>
          </Section>

          <Section title="9. Cookies">
            <p>
              La plataforma utiliza cookies estrictamente necesarias para el
              funcionamiento (sesión y preferencias). No usamos cookies de tracking
              publicitario sin tu consentimiento.
            </p>
          </Section>

          <Section title="10. Modificaciones">
            <p>
              Esta política puede actualizarse para reflejar cambios legales o de
              servicio. Cualquier modificación relevante será informada en esta misma
              página.
            </p>
          </Section>

          <Section title="11. Contacto">
            <p>
              Para cualquier consulta sobre esta política o el tratamiento de tus
              datos, escríbenos desde la{" "}
              <Link to="/contacto" className="text-brand hover-line">página de contacto</Link>.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl text-ash-100 mb-3 border-b border-ink-500 pb-2">{title}</h2>
      <div className="text-ash-100 space-y-3">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-brand mt-1.5">—</span>
      <span>{children}</span>
    </li>
  );
}
